import { useEffect, useState } from 'react';
import { getBalance, AccountNode } from '../services/apiService';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface RecebivelCliente {
  cliente: string;
  full_path: string;
  totalBalance: number;
  competencias: RecebivelCompetencia[];
}

export interface RecebivelCompetencia {
  competencia: string;
  balance: number;
  status: 'pendente' | 'recebido';
  full_path: string;
}

export interface ContasReceberData {
  aFaturar: RecebivelCliente[];
  contasReceber: RecebivelCliente[];
  totals: {
    aFaturar: number;
    contasReceber: number;
    grandTotal: number;
  };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function brl(node: AccountNode): number {
  return node.balances?.BRL ?? node.balances?.['R$'] ?? 0;
}

const COMPETENCIA_RE = /^\d{4}-\d{2}$/;

// ---------------------------------------------------------------------------
// Pure transformation functions
// ---------------------------------------------------------------------------

/**
 * Transforms an Ativos:A Faturar or Ativos:Contas a Receber tree
 * into a list of clients with their competências.
 *
 * Structure: {Root} → {Cliente} → {YYYY-MM}
 */
export function groupByCliente(rootNode: AccountNode | undefined): RecebivelCliente[] {
  if (!rootNode) return [];

  const clients: RecebivelCliente[] = [];

  for (const clienteNode of rootNode.children) {
    const totalBalance = brl(clienteNode);
    const competencias: RecebivelCompetencia[] = [];

    for (const child of clienteNode.children) {
      if (!COMPETENCIA_RE.test(child.account)) continue;

      const balance = brl(child);
      competencias.push({
        competencia: child.account,
        balance,
        status: Math.abs(balance) < 0.01 ? 'recebido' : 'pendente',
        full_path: child.full_path,
      });
    }

    // Sort competências desc (most recent first)
    competencias.sort((a, b) => b.competencia.localeCompare(a.competencia));

    // Include client even without competência sub-accounts (direct balance)
    if (Math.abs(totalBalance) >= 0.01 || competencias.length > 0) {
      clients.push({
        cliente: clienteNode.account,
        full_path: clienteNode.full_path,
        totalBalance,
        competencias,
      });
    }
  }

  // Sort by absolute balance desc
  clients.sort((a, b) => Math.abs(b.totalBalance) - Math.abs(a.totalBalance));
  return clients;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useContasReceberData() {
  const [data, setData] = useState<ContasReceberData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    Promise.all([
      getBalance({ account: 'Ativos:A Faturar' }),
      getBalance({ account: 'Ativos:Contas a Receber' }),
    ])
      .then(([aFaturarRes, contasReceberRes]) => {
        if (cancelled) return;

        const aFaturarNode = aFaturarRes.accounts[0];
        const contasReceberNode = contasReceberRes.accounts[0];

        const aFaturar = groupByCliente(aFaturarNode);
        const contasReceber = groupByCliente(contasReceberNode);

        const totAFaturar = aFaturar.reduce((s, c) => s + c.totalBalance, 0);
        const totContasReceber = contasReceber.reduce((s, c) => s + c.totalBalance, 0);

        setData({
          aFaturar,
          contasReceber,
          totals: {
            aFaturar: totAFaturar,
            contasReceber: totContasReceber,
            grandTotal: totAFaturar + totContasReceber,
          },
        });
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Erro ao carregar contas a receber');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, []);

  return { data, loading, error };
}
